// MiniCalendar.js — pure MUI, no external dependencies
// Props:
//   value      {Date|null}          single-mode: selected date
//              {[Date|null, Date|null]} range-mode: [startDate, endDate]
//   onChange   {fn(Date)}           single-mode: called with selected Date
//              {fn([Date,Date|null])} range-mode: called with [start, end] (end may be null)
//   activeDays {number|null}        bitmask: bit0=Mon…bit6=Sun (null = all days allowed)
//   minDate    {Date}               days before this are disabled (default = today)
//   rangeMode  {boolean}            enable two-date range selection (default = false)

import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ChevronLeftIcon  from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const TERRACOTTA      = '#C4622D';
const WALNUT          = '#2C1F14';
const RANGE_BG        = 'rgba(196,98,45,0.12)';
const RANGE_HOVER_BG  = 'rgba(196,98,45,0.07)';

const DAY_LABELS  = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTH_NAMES = ['January','February','March','April','May','June',
    'July','August','September','October','November','December'];
// JS getDay(): 0=Sun…6=Sat  →  ISO bitmask bit: 0=Mon…6=Sun
const JS_TO_BIT = [6, 0, 1, 2, 3, 4, 5];

function isSame(a, b) {
    return a && b &&
        a.getFullYear() === b.getFullYear() &&
        a.getMonth()    === b.getMonth()    &&
        a.getDate()     === b.getDate();
}

function dateOnly(d) {
    if (!d) return null;
    const c = new Date(d); c.setHours(0, 0, 0, 0); return c;
}

export default function MiniCalendar({
    value, onChange, activeDays = null, minDate, rangeMode = false, maxRange = null, sx = {},
}) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const floor = minDate || today;

    // In rangeMode value = [start, end]; in single mode value = Date|null
    const rangeStart = rangeMode && Array.isArray(value) ? dateOnly(value[0]) : null;
    const rangeEnd   = rangeMode && Array.isArray(value) ? dateOnly(value[1]) : null;
    const singleVal  = !rangeMode ? value : null;

    const maxRangeEnd = (rangeMode && rangeStart && maxRange !== null)
        ? new Date(rangeStart.getTime() + maxRange * 24 * 60 * 60 * 1000)
        : null;

    const [viewYear,  setViewYear]  = useState(() => {
        const ref = rangeMode ? (rangeStart || today) : (singleVal || today);
        return ref.getFullYear();
    });
    const [viewMonth, setViewMonth] = useState(() => {
        const ref = rangeMode ? (rangeStart || today) : (singleVal || today);
        return ref.getMonth();
    });
    const [hoverDate, setHoverDate] = useState(null);

    const prevMonth = () => viewMonth === 0  ? (setViewMonth(11), setViewYear(y => y - 1)) : setViewMonth(m => m - 1);
    const nextMonth = () => viewMonth === 11 ? (setViewMonth(0),  setViewYear(y => y + 1)) : setViewMonth(m => m + 1);
    const prevYear  = () => setViewYear(y => y - 1);
    const nextYear  = () => setViewYear(y => y + 1);

    // Build cell array — Monday-first, padded with nulls
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const startPad    = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;
    const cells = [
        ...Array(startPad).fill(null),
        ...Array.from({ length: daysInMonth }, (_, i) => new Date(viewYear, viewMonth, i + 1)),
    ];
    while (cells.length % 7 !== 0) cells.push(null);

    const isAllowed = (date) => {
        if (!date) return false;
        const d = new Date(date); d.setHours(0, 0, 0, 0);
        if (d < floor) return false;
        if (activeDays !== null) {
            const bit = JS_TO_BIT[date.getDay()];
            if (!(activeDays & (1 << bit))) return false;
        }
        if (rangeMode && rangeStart && !rangeEnd && maxRangeEnd) {
            if (d > maxRangeEnd) return false;
        }
        return true;
    };

    const handleClick = (date) => {
        if (!isAllowed(date)) return;
        if (!rangeMode) { onChange(date); return; }

        if (!rangeStart || rangeEnd) {
            // No start yet, or both already set → start a new range
            onChange([date, null]);
        } else if (isSame(date, rangeStart)) {
            // Clicking start again → clear
            onChange([null, null]);
        } else if (date < rangeStart) {
            // Clicked before start → new start, clear end
            onChange([date, null]);
        } else {
            // Valid end — clamp to maxRangeEnd if a cap is in effect
            const finalEnd = (maxRangeEnd && date > maxRangeEnd) ? maxRangeEnd : date;
            onChange([rangeStart, finalEnd]);
        }
    };

    // Effective end for hover preview (clamped to maxRangeEnd when cap is active)
    const clampedHover = (rangeMode && rangeStart && !rangeEnd && hoverDate && maxRangeEnd)
        ? (hoverDate > maxRangeEnd ? maxRangeEnd : hoverDate)
        : hoverDate;

    const effectiveEnd = rangeMode && rangeStart && !rangeEnd && clampedHover && clampedHover > rangeStart
        ? clampedHover : rangeEnd;

    const CELL = 38;

    return (
        <Box sx={{
            bgcolor: 'white', borderRadius: 2,
            border: '1px solid rgba(196,98,45,0.15)',
            p: 2, userSelect: 'none',
            width: 7 * CELL + 32,
            ...sx,
        }}>
            {/* Year navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5, gap: 0.5 }}>
                <IconButton size="small" onClick={prevYear}  sx={{ color: TERRACOTTA, p: 0.5 }}>
                    <ChevronLeftIcon  sx={{ fontSize: '1rem' }} />
                    <ChevronLeftIcon  sx={{ fontSize: '1rem', ml: -1 }} />
                </IconButton>
                <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT, minWidth: 36, textAlign: 'center' }}>
                    {viewYear}
                </Typography>
                <IconButton size="small" onClick={nextYear}  sx={{ color: TERRACOTTA, p: 0.5 }}>
                    <ChevronRightIcon sx={{ fontSize: '1rem', mr: -1 }} />
                    <ChevronRightIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
            </Box>

            {/* Month navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <IconButton size="small" onClick={prevMonth} sx={{ color: TERRACOTTA }}>
                    <ChevronLeftIcon />
                </IconButton>
                <Typography variant="subtitle1" fontWeight={800} sx={{ color: WALNUT }}>
                    {MONTH_NAMES[viewMonth]}
                </Typography>
                <IconButton size="small" onClick={nextMonth} sx={{ color: TERRACOTTA }}>
                    <ChevronRightIcon />
                </IconButton>
            </Box>

            {/* Day-of-week header row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(7, ${CELL}px)`, mb: 0.5 }}>
                {DAY_LABELS.map(l => (
                    <Box key={l} sx={{ width: CELL, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.7rem' }}>
                            {l}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Day cells */}
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(7, ${CELL}px)` }}>
                {cells.map((date, i) => {
                    if (!date) return <Box key={`e-${i}`} sx={{ width: CELL, height: CELL }} />;

                    const allowed  = isAllowed(date);
                    const isToday  = isSame(date, today);

                    // ── Single-mode ──────────────────────────────────────────
                    const selected = !rangeMode && isSame(date, singleVal);

                    // ── Range-mode ───────────────────────────────────────────
                    const isStart   = rangeMode && isSame(date, rangeStart);
                    const isEnd     = rangeMode && isSame(date, effectiveEnd);
                    const isEndReal = rangeMode && isSame(date, rangeEnd);
                    const inRange   = rangeMode && rangeStart && effectiveEnd &&
                        date > rangeStart && date < effectiveEnd;
                    const inHover   = rangeMode && rangeStart && !rangeEnd && hoverDate &&
                        hoverDate > rangeStart && date > rangeStart && date < hoverDate;

                    const isCircle  = selected || isStart || isEnd;
                    const circleBg  = isCircle ? TERRACOTTA : 'transparent';

                    // Band behind the circle for range
                    let bandBg = 'transparent', bandRadius = '0';
                    if (rangeMode) {
                        if (inRange)  { bandBg = RANGE_BG; bandRadius = '0'; }
                        if (inHover && !inRange) { bandBg = RANGE_HOVER_BG; bandRadius = '0'; }
                        if (isStart && (isEnd || effectiveEnd)) { bandBg = RANGE_BG; bandRadius = '50% 0 0 50%'; }
                        if (isEnd && rangeStart && !isSame(rangeStart, effectiveEnd)) {
                            bandBg = isEndReal ? RANGE_BG : RANGE_HOVER_BG;
                            bandRadius = '0 50% 50% 0';
                        }
                    }

                    return (
                        <Box
                            key={date.toISOString()}
                            onClick={() => handleClick(date)}
                            onMouseEnter={() => rangeMode && setHoverDate(dateOnly(date))}
                            onMouseLeave={() => rangeMode && setHoverDate(null)}
                            sx={{
                                width: CELL, height: CELL,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: allowed ? 'pointer' : 'default',
                                bgcolor: bandBg,
                                borderRadius: bandRadius,
                            }}
                        >
                            <Box sx={{
                                width:  CELL - 6, height: CELL - 6,
                                borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: circleBg,
                                border: isToday && !isCircle
                                    ? `2px solid ${TERRACOTTA}`
                                    : '2px solid transparent',
                                transition: 'background 0.15s',
                                '&:hover': allowed && !isCircle
                                    ? { bgcolor: 'rgba(196,98,45,0.13)' }
                                    : {},
                            }}>
                                <Typography sx={{
                                    fontSize: '0.82rem',
                                    fontWeight: isCircle ? 800 : isToday ? 700 : 400,
                                    lineHeight: 1,
                                    color: isCircle  ? 'white'
                                        : !allowed  ? 'rgba(0,0,0,0.22)'
                                        : isToday   ? TERRACOTTA
                                        : WALNUT,
                                }}>
                                    {date.getDate()}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {/* Legend when activeDays restricts */}
            {activeDays !== null && activeDays !== 127 && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(196,98,45,0.1)' }}>
                    <Typography variant="caption" color="text.secondary">
                        Available: {['Mon','Tue','Wed','Thu','Fri','Sat','Sun']
                        .filter((_, i) => activeDays & (1 << i)).join(', ')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}